<template>
  <ul :class="['owc-lower-footer-legal', { 'owc-lower-footer-legal--centered': qrCodeImage }]">
    <li v-if="qrCodeImage" class="owc-lower-footer-legal__qrCode">
      <GenericImage class="owc-lower-footer-legal__qrCode-img" :imageData="qrCodeData" />
    </li>
    <li v-if="legalLicense" class="owc-lower-footer-legal__license">
      <a
        v-if="legalLicense.url"
        class="owc-lower-footer-legal__link"
        :href="legalLicense.url"
        :target="legalLicense.target"
        data-track="false"
        @click="handleTracking(legalLicense.url, legalLicense.label)"
      >
        {{ legalLicense.label }}
      </a>
      <span v-else class="owc-lower-footer-legal__text">{{ legalLicense.label }}</span>
    </li>
    <li class="owc-lower-footer-legal__item" v-for="(legalItem, index) in legalItems" :key="index">
      <a
        v-if="legalItem.url"
        class="owc-lower-footer-legal__link"
        :href="legalItem.url"
        :target="legalItem.target"
        data-track="false"
        @click="handleTracking(legalItem.url, legalItem.label)"
      >
        {{ legalItem.label }}
      </a>
      <span v-else class="owc-lower-footer-legal__text">
        {{ legalItem.label }}
      </span>
    </li>
  </ul>
</template>

<script lang="ts">
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import {
  LegalSectionData,
  LegalSectionMethods,
  LegalSectionComputed,
  LegalSectionProps,
} from './LegalSection';
import GenericImage from '@owc-packages/GenericImage/src/GenericImage.vue';
import { componentMode } from './Footer.config';

/**
 * `Legal Section Footer` - Legal Section Footer for OneWeb - Legal Section contains the legal information of the website, this components exists inside the Lower Footer Component
 */

export default ComponentInitializeDispatcher.extend<
  LegalSectionData,
  LegalSectionMethods,
  LegalSectionComputed,
  LegalSectionProps
>({
  name: 'owc-legal-section',
  components: {
    GenericImage,
  },
  props: {
    legalSection: {
      type: Object,
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.qrCodeImage) {
        this.sendMessageToParentContainer();
      }
    });
  },
  computed: {
    isEdit: function(): boolean {
      return componentMode.isEditMode;
    },
    legalItems: function() {
      return this.isEdit ? this.componentData?.items : this.legalSection?.items;
    },
    legalLicense: function() {
      return this.isEdit ? this.componentData?.license : this.legalSection?.license;
    },
    qrCodeData: function() {
      return this.isEdit ? this.componentData?.qrCode : this.legalSection?.qrCode;
    },
    qrCodeImage: function() {
      return this.qrCodeData?.source;
    },
  },
  methods: {
    sendMessageToParentContainer: function() {
      this.$el.dispatchEvent(
        new CustomEvent('qrCodeActive', {
          composed: true,
          bubbles: true,
        }),
      );
    },

    handleTracking: function(linkUrl: string, linkLabel: string) {
      const eventLabel = this.getEventLabel(linkUrl, 'legal', linkLabel);

      this.handleCustomTracking(this.$el, eventLabel, 'link', 'click', 'navigation', linkUrl);
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/rtl-utils';

$lowerModuleName: 'owc-lower-footer-legal';

.#{$lowerModuleName} {
  @include wb-type-copy-secondary;

  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;

  order: 1;
  width: 100%;

  @include breakpoint-from(mq5) {
    order: 0;
    width: auto;

    &--centered {
      align-items: center;

      .#{$lowerModuleName}__item {
        margin-bottom: 0;
      }
    }
  }

  &__item {
    margin-bottom: var(--wb-spacing-xs);

    word-break: normal;
    overflow-wrap: anywhere;

    &:not(:last-child) {
      margin-right: var(--wb-spacing-s);
    }
  }
  &__license {
    order: 1;
    width: 100%;

    word-break: normal;
    overflow-wrap: anywhere;

    @include breakpoint-from(mq5) {
      order: 0;
      width: auto;

      margin-right: var(--wb-spacing-xs);
    }
  }
  &__qrCode {
    order: 2;
    width: 100%;

    margin-top: var(--wb-spacing-xs);

    text-align: center;

    @include breakpoint-from(mq5) {
      order: 0;
      width: auto;

      margin-top: 0;
      margin-right: var(--wb-spacing-xs);
    }

    img {
      width: auto;
      max-width: 90px;
      height: auto;
    }

    @include breakpoint-from(mq5) {
      width: auto;
    }
  }
  &__link {
    @include wb-link;
    @include wb-small-or-tiny-link;
    @include wb-small-or-tiny-link--theme-dark;
  }
  &__text {
    color: var(--wb-white);
  }
}

@include rtl-rule('.owc-lower-footer-legal', false) {
  &__license {
    @include breakpoint-from(mq5) {
      margin-right: 0;
      margin-left: var(--wb-spacing-xs);
    }
  }
  &__item {
    &:not(:last-child) {
      margin-right: 0;
      margin-left: var(--wb-spacing-s);
    }
  }
}
</style>
